import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import * as FileSaver from "file-saver";
import moment from "moment";
import { toast } from "react-toastify";
import AppLoader from "../component/AppLoader";
import Actions from "../Redux/action";

const EXCEL_EXTENSION = ".xlsx";
const EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

export const HostelDashboardDetails = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { hostelId, status, period, hostelName } = location.state || {};

    const [data, setData] = useState([]);
    const [isLoading, setLoading] = useState(true);

    const fetchData = useCallback(() => {
        if (!hostelId || !status) return;
        const callback = (response) => {
            if (response?.status_code === 0) {
                setData(response?.response || []);
            } else {
                toast.error(response?.message || "Failed to load details");
            }
            setLoading(false);
        };
        setLoading(true);
        dispatch(
            Actions.hostelDashboardDetailsAction(
                { hostel_id: hostelId, status: status?.trim(), period: period || "today" },
                callback
            )
        );
    }, [dispatch, hostelId, status, period]);

    useEffect(() => {
        if (!hostelId || !status) {
            toast.error("Invalid navigation. Redirecting to dashboard.");
            navigate("/dashboard");
            return;
        }
        fetchData();
    }, [hostelId, status, fetchData, navigate]);

    const handleBack = () => {
        navigate("/dashboard");
    };

    const handleExport = () => {
        if (!data?.length) {
            toast.warning("No data to export");
            return;
        }
        const exportData = data.map((row, idx) => ({
            "#": idx + 1,
            "Reg No / Member ID": row.member_id,
            Name: row.name,
            "Last Activity Time": row.time ? moment(row.time).format("DD-MM-YYYY HH:mm:ss") : "-",
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = { Sheets: { Data: worksheet }, SheetNames: ["Data"] };
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: EXCEL_TYPE });
        const fileName = `${hostelName || "Hostel"}_${status}_${period || "today"}_${new Date().getTime()}${EXCEL_EXTENSION}`;
        FileSaver.saveAs(blob, fileName);
        toast.success("Downloaded successfully");
    };

    if (!hostelId || !status) return null;

    return (
        <>
            {isLoading && <AppLoader />}
            <div className="ecommerce-widget">
                <div className="page-header">
                    <div className="row">
                        <div className="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h2 className="pageheader-title mb-0">
                                {hostelName} - {status}
                            </h2>
                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleExport}
                                    disabled={isLoading || !data?.length}
                                >
                                    <i className="fas fa-download me-2"></i>
                                    Export to Excel
                                </button>
                                <button type="button" className="btn btn-outline-secondary" onClick={handleBack}>
                                    <i className="fas fa-arrow-left me-2"></i>
                                    Back to Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card border-3 border-top border-top-primary mt-3">
                    <div className="card-body">
                        {isLoading ? (
                            <div className="text-center py-5">Loading...</div>
                        ) : data?.length === 0 ? (
                            <p className="text-muted text-center mb-0 py-5">No records found.</p>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Reg No / Member ID</th>
                                            <th>Name</th>
                                            <th>Last Activity Time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.map((row, idx) => (
                                            <tr key={idx}>
                                                <td>{idx + 1}</td>
                                                <td>{row.member_id}</td>
                                                <td>{row.name}</td>
                                                <td>{row.time ? moment(row.time).format("DD-MM-YYYY HH:mm:ss") : "-"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
